import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Autocomplete, ImageList, ImageListItem, ImageListItemBar,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, PhotoCamera as CameraIcon,
  Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon,
  ZoomIn as ZoomIcon, CloudUpload as UploadIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import { FileUpload } from '../../components/ui/FileUpload';

const typeLabels: Record<string, string> = {
  clinical_photo: 'Foto Clínica', xray: 'Radiografia', panoramic: 'Panorâmica',
  periapical: 'Periapical', ct_scan: 'Tomografia', mri: 'Ressonância',
  intraoral: 'Intraoral', extraoral: 'Extraoral', before_after: 'Antes/Depois',
  document: 'Documento', other: 'Outro',
};

const ImageGallery: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  const [form, setForm] = useState<any>({
    patient_id: '', image_type: 'clinical_photo', title: '', description: '',
    file_url: '', file_name: '', file_type: '', file_size: 0,
    thumbnail_url: '', category: '', tooth_number: '',
    capture_date: new Date().toISOString().split('T')[0], tags: '',
  });

  useEffect(() => { loadImages(); }, [search, filterType]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (search) params.search = search;
      if (filterType) params.image_type = filterType;
      const { data } = await axios.get(`${getApiUrl()}/clinic/patient-images`, { params });
      setImages(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/clinic/prescriptions-search/patients`, { params: { search: q } });
      setPatientOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleUploadFile = async (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await axios.post(`${getApiUrl()}/clinic/patient-images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (data.success && data.data) {
        setForm({ 
          ...form, 
          file_url: data.data.file_url,
          file_name: data.data.file_name || file.name,
          file_type: data.data.file_type || file.type,
          file_size: data.data.file_size || file.size
        });
        setSnackbar({ open: true, message: 'Imagem enviada com sucesso!', severity: 'success' });
        setUploadKey(prev => prev + 1);
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao fazer upload', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.file_url) {
      setSnackbar({ open: true, message: 'Envie uma imagem antes de salvar', severity: 'error' });
      return;
    }
    if (!form.file_name) {
      setSnackbar({ open: true, message: 'Nome do arquivo não encontrado. Por favor, faça upload novamente.', severity: 'error' });
      return;
    }
    try {
      const saveData = { 
        ...form, 
        file_url: form.file_url,
        file_name: form.file_name,
        file_type: form.file_type || 'image/jpeg',
        file_size: form.file_size || 0,
        tags: form.tags ? form.tags.split(',').map((t: string) => t.trim()) : [] 
      };
      await axios.post(`${getApiUrl()}/clinic/patient-images`, saveData);
      setSnackbar({ open: true, message: 'Imagem registrada!', severity: 'success' });
      setOpenDialog(false); resetForm(); loadImages();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir imagem?')) return;
    try {
      await axios.delete(`${getApiUrl()}/clinic/patient-images/${id}`);
      setSnackbar({ open: true, message: 'Excluída!', severity: 'success' }); loadImages();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleToggleFavorite = async (id: string) => {
    try {
      await axios.patch(`${getApiUrl()}/clinic/patient-images/${id}/favorite`);
      loadImages();
    } catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setForm({
      patient_id: '', image_type: 'clinical_photo', title: '', description: '',
      file_url: '', file_name: '', file_type: '', file_size: 0,
      thumbnail_url: '', category: '', tooth_number: '',
      capture_date: new Date().toISOString().split('T')[0], tags: '',
    });
    setUploadKey(prev => prev + 1);
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Galeria de Imagens Clínicas</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Fotos clínicas, radiografias e documentos de imagem</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadImages} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }} onClick={() => { resetForm(); setOpenDialog(true); }}>
            Nova Imagem
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : images.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
          <CameraIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography sx={{ color: '#94a3b8' }}>Nenhuma imagem encontrada</Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {images.map((img) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
              <Card sx={{ borderRadius: 3, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
                <Box sx={{ height: 200, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                  onClick={() => setPreviewImage(img)}>
                  {img.file_url ? (
                    <img src={img.file_url} alt={img.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <CameraIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
                  )}
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleFavorite(img.id); }}
                      sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#fef2f2' } }}>
                      {img.is_favorite ? <FavoriteIcon sx={{ color: '#ef4444', fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
                    </IconButton>
                  </Box>
                </Box>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }} noWrap>{img.title || 'Sem título'}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{img.patient_name || '-'}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Chip label={typeLabels[img.image_type] || img.image_type} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#14B8A615', color: '#14B8A6' }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {img.capture_date ? new Date(img.capture_date + 'T12:00:00').toLocaleDateString('pt-BR') : ''}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{previewImage?.title || 'Imagem'}</Typography>
          <Box>
            <IconButton onClick={() => { handleDelete(previewImage?.id); setPreviewImage(null); }} sx={{ color: '#ef4444' }}><DeleteIcon /></IconButton>
            <IconButton onClick={() => setPreviewImage(null)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewImage?.file_url && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img src={previewImage.file_url} alt={previewImage.title} style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }} />
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b' }}>Paciente</Typography><Typography>{previewImage?.patient_name || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b' }}>Tipo</Typography><Typography>{typeLabels[previewImage?.image_type] || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b' }}>Data</Typography><Typography>{previewImage?.capture_date ? new Date(previewImage.capture_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</Typography></Grid>
            {previewImage?.tooth_number && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b' }}>Dente</Typography><Typography>{previewImage.tooth_number}</Typography></Grid>}
            {previewImage?.description && <Grid item xs={12}><Typography variant="caption" sx={{ color: '#64748b' }}>Descrição</Typography><Typography>{previewImage.description}</Typography></Grid>}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Nova Imagem</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={patientOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchPatients(v)} onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                onOpen={() => searchPatients('')} renderInput={(params) => <TextField {...params} label="Paciente" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Tipo</InputLabel>
                <Select value={form.image_type} label="Tipo" onChange={(e) => setForm({ ...form, image_type: e.target.value })}>
                  {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Data" value={form.capture_date}
                onChange={(e) => setForm({ ...form, capture_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#64748b', mb: 1, display: 'block' }}>Anexar Imagem</Typography>
              <FileUpload
                key={uploadKey}
                onUpload={handleUploadFile}
                uploading={uploading}
              />
              {form.file_url && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#14B8A6', fontWeight: 600 }}>✓ Imagem anexada</Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Dente (nº)" value={form.tooth_number} onChange={(e) => setForm({ ...form, tooth_number: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Tags (separadas por vírgula)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ImageGallery;
